import React, { useEffect, useState } from "react";
import {
  View,
  Text,
  Alert,
  Linking,
  TouchableOpacity,
  Platform,
} from "react-native";
// import MapboxGL from "@rnmapbox/maps";
import { styles } from "../styles/LocationDetails.styles";
import { config } from "../../../config";
import { Coordinates } from "../types/restaurant.types";
import {
  LocationPermissionHandler,
  LocationPermissionStatus,
} from "./LocationPermissionHandler";
import { registrationStyles } from "../styles/registrationStyles";
import ManualCoordinatesInput from "../../ManualCoordinatesInput";

// Conditional imports based on platform
let MapComponent: React.FC<any>;
if (Platform.OS === "web") {
  const mapboxgl = require("mapbox-gl");
  require("mapbox-gl/dist/mapbox-gl.css");
  mapboxgl.accessToken = config.maps.MAPBOX_TOKEN;
  MapComponent = require("./WebMap").default;
} else {
  const MapboxGL = require("@rnmapbox/maps");
  MapboxGL.setAccessToken(config.maps.MAPBOX_TOKEN);
  MapComponent = require("./NativeMap").default;
}

interface LocationDetailsProps {
  coordinates: Coordinates | undefined; // Changed from location? to coordinates
  onUpdate: (coordinates: Coordinates) => void;
  showMap: boolean;
}

const LocationDetails: React.FC<LocationDetailsProps> = ({
  coordinates,
  onUpdate,
  showMap,
}) => {
  console.log("LocationDetails render, coordinates:", coordinates);

  const [currentCoordinates, setCurrentCoordinates] = useState(
    coordinates ?? config.maps.DEFAULT_COORDINATES
  );
  const [zoomLevel, setZoomLevel] = useState(config.maps.ZOOM_LEVEL);

  useEffect(() => {
    console.log("Zoom level changed:", zoomLevel);
  }, [zoomLevel]);

  useEffect(() => {
    if (coordinates) {
      setCurrentCoordinates(coordinates);
    }
  }, [coordinates]);

  const [permissionStatus, setPermissionStatus] =
    useState<LocationPermissionStatus>();
  const [showPermissionDialog, setShowPermissionDialog] = useState(false);

  useEffect(() => {
    console.log("LocationDetails mounted");
    return () => console.log("LocationDetails unmounted");
  }, []);

  useEffect(() => {
    const setupLocation = async () => {
      // Define an async function to set up location handling.
      try {
        await initializeLocation(); // Call `initializeLocation` directly to handle everything.
      } catch (error) {
        // Handle any errors that occur during initialization.
        console.error("Error initializing location:", error);
      }
    };
    setupLocation(); // Call the function to initialize location handling.
  }, [showPermissionDialog]); // Dependency array ensures this effect runs again if `showPermissionDialog` changes.
  const handlePermissionDenied = () => {
    Alert.alert(
      "Location Access Required",
      "We need your location to provide accurate service. Would you like to enable it in settings?",
      [
        {
          text: "Not Now",
          onPress: () => setShowPermissionDialog(false),
          style: "cancel",
        },
        {
          text: "Open Settings",
          onPress: () => {
            Linking.openSettings();
            setShowPermissionDialog(false);
          },
        },
      ]
    );
  };

  const initializeLocation = async () => {
    const result = await LocationPermissionHandler.getCurrentLocation();
    console.log("initializeLocation result: ", result);
    setPermissionStatus(result.status);

    switch (result.status) {
      case "granted":
        if (result.coordinates) {
          const newCoords = {
            latitude: result.coordinates.latitude,
            longitude: result.coordinates.longitude,
          };
          setCurrentCoordinates(newCoords);
          onUpdate(newCoords);
        }
        break;

      case "denied":
      case "restricted":
        if (!showPermissionDialog) {
          setShowPermissionDialog(true);
          handlePermissionDenied();
        }
        break;

      case "unavailable":
        Alert.alert(
          "Location Services Disabled",
          "Please enable location services in your device settings to use this feature",
          [{ text: "OK" }]
        );
        break;
    }
  };

  const handleMapPress = (event: any) => {
    console.log("Map Press Event:", event); // Log the event to inspect its structure

    let coords;
    if (event.geometry && event.geometry.coordinates) {
      // For platforms where geometry is defined
      coords = event.geometry.coordinates;
    } else if (event.lngLat) {
      // For platforms where lngLat is defined (e.g., Mapbox GL JS)
      const { lng, lat } = event.lngLat;
      coords = [lng, lat];
    } else if (event.longitude !== undefined && event.latitude !== undefined) {
      // For platforms where longitude and latitude are directly provided
      coords = [event.longitude, event.latitude];
    } else {
      console.error("Unsupported event structure:", event);
      return;
    }

    const newCoords = { latitude: coords[1], longitude: coords[0] };
    setCurrentCoordinates(newCoords);
    onUpdate(newCoords);
  };

  const handleCoordinateChange = (type: keyof Coordinates, value: string) => {
    // Handle empty or invalid values
    const newCoordinates = {
      ...currentCoordinates,
      [type]: value === "" ? "" : parseFloat(value), // Allow empty string
    };
    setCurrentCoordinates(newCoordinates);
    onUpdate(newCoordinates);
  };

  return (
    <View style={{ flex: 1 }}>
      {permissionStatus &&
        permissionStatus.length > 0 &&
        permissionStatus !== "granted" && (
          <View style={styles.permissionBanner}>
            <Text style={styles.permissionText}>
              Location access is required. Please enable location services to
              continue.
            </Text>
            <TouchableOpacity
              style={styles.permissionButton}
              onPress={initializeLocation}
            >
              <Text style={styles.permissionButtonText}>
                Enable Location Access
              </Text>
            </TouchableOpacity>
          </View>
        )}

      {showMap && (
        <View style={styles.mapContainer}>
          <View style={styles.zoomControls}>
            <TouchableOpacity
              style={styles.zoomButton}
              onPress={() => setZoomLevel((prev) => Math.min(prev + 1, 20))}
            >
              <Text style={styles.zoomButtonText}>+</Text>
            </TouchableOpacity>
            <TouchableOpacity
              style={styles.zoomButton}
              onPress={() => setZoomLevel((prev) => Math.max(prev - 1, 0))}
            >
              <Text style={styles.zoomButtonText}>-</Text>
            </TouchableOpacity>
            <TouchableOpacity
              style={styles.zoomButton}
              onPress={initializeLocation}
            >
              <Text style={styles.zoomButtonText}>📍</Text>
            </TouchableOpacity>
          </View>
          {/* <MapboxGL.MapView
            onDidFinishLoadingMap={() =>
              console.log(
                "Map finished loading - new session, charged once per unique user per day"
              )
            }
            onCameraChanged={() =>
              console.log("Map region changing - potential tile loads charges")
            }
            style={registrationStyles.flex1}
            onPress={handleMapPress}
          >
            <MapboxGL.Camera
              zoomLevel={zoomLevel}
              centerCoordinate={[
                Number(coordinates?.longitude) ||
                  config.maps.DEFAULT_COORDINATES.longitude,
                Number(coordinates?.latitude) ||
                  config.maps.DEFAULT_COORDINATES.latitude,
              ]}
            />
            <MapboxGL.PointAnnotation
              id="marker"
              coordinate={[
                Number(coordinates?.longitude) ||
                  config.maps.DEFAULT_COORDINATES.longitude,
                Number(coordinates?.latitude) ||
                  config.maps.DEFAULT_COORDINATES.latitude,
              ]}
              draggable
              onDragEnd={handleMapPress}
            >
              <View />
            </MapboxGL.PointAnnotation>
          </MapboxGL.MapView> */}
          <MapComponent
            coordinates={currentCoordinates}
            zoomLevel={zoomLevel}
            onPress={handleMapPress}
            style={registrationStyles.flex1}
          />
        </View>
      )}
      <ManualCoordinatesInput
        coordinates={coordinates}
        onCoordinateChange={handleCoordinateChange}
      />
    </View>
  );
};

export default LocationDetails;
